import { useCallback, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiTokenRequest, apiConfig } from '../authConfig';
import { apiErrorFromResponse, userMessageForError } from '../utils/apiError';
import { impersonationHeaders } from '../utils/impersonationFetch';

/**
 * The caller's own shared dashboard links for one project.
 *   list:   GET    /api/v1/me/shared-dashboard-links        -> { links: [{ id, projectId, visibleTileIds,
 *           expiresAt, pinnedAsOf, revokedAt, createdAt, passwordProtected, status, label,
 *           accessCount, lastAccessedAt, aiTilesAtRisk }] }
 *   revoke: DELETE /api/v1/shared-dashboard-links/:id       -> { link: { id, revokedAt } }
 * (dashboard.controller.ts; creator or platform_admin may revoke, the API enforces it.)
 *
 * The share URL is deliberately never read from the list: a link is shown once, in the creation
 * modal, and the list is a management view (status, scope, dates, activity, revoke). `status` is the
 * server's (active | revoked | expired | legacy_reissue); `accessCount` / `lastAccessedAt` null means
 * UNKNOWN (the audit trail could not say), which the UI shows as "Unknown", never as 0.
 * The list is per user across projects, so rows are narrowed to `projectId` for display only.
 * `X-Impersonation-Id` is attached when an LA impersonation session is active so the revoke is
 * made as the effective actor.
 */
const SERVER_STATUSES = new Set(['active', 'revoked', 'expired', 'legacy_reissue']);

export function linkStatus(link, now = Date.now()) {
  if (SERVER_STATUSES.has(link.status)) return link.status;
  if (link.revokedAt) return 'revoked';
  const exp = new Date(link.expiresAt).getTime();
  if (!Number.isNaN(exp) && exp <= now) return 'expired';
  return 'active';
}

function normaliseLink(raw) {
  return {
    id: raw.id,
    projectId: raw.projectId,
    tileIds: Array.isArray(raw.visibleTileIds) ? raw.visibleTileIds : [],
    createdAt: raw.createdAt ?? null,
    expiresAt: raw.expiresAt ?? null,
    revokedAt: raw.revokedAt ?? null,
    passwordProtected: typeof raw.passwordProtected === 'boolean' ? raw.passwordProtected : null,
    status: SERVER_STATUSES.has(raw.status) ? raw.status : null,
    label: typeof raw.label === 'string' && raw.label.trim() ? raw.label.trim() : null,
    accessCount: Number.isFinite(raw.accessCount) ? raw.accessCount : null,
    lastAccessedAt: raw.lastAccessedAt ?? null,
    aiTilesAtRisk: Array.isArray(raw.aiTilesAtRisk) ? raw.aiTilesAtRisk : [],
  };
}

export function useSharedDashboardLinks(projectId) {
  const { instance, accounts } = useMsal();
  const [status, setStatus] = useState('loading'); // 'loading' | 'error' | 'empty' | 'ready'
  const [links, setLinks] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);

  const getHeaders = useCallback(async () => {
    const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account: accounts[0] });
    return { Authorization: `Bearer ${tokenResponse.accessToken}`, ...impersonationHeaders() };
  }, [instance, accounts]);

  const load = useCallback(async () => {
    if (!projectId) return;
    setStatus('loading');
    setErrorMessage(null);
    try {
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/me/shared-dashboard-links`, { headers: await getHeaders() });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.success) throw apiErrorFromResponse(res, json);
      const rows = (Array.isArray(json.data?.links) ? json.data.links : [])
        .filter((l) => l && l.projectId === projectId)
        .map(normaliseLink)
        .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
      setLinks(rows);
      setStatus(rows.length ? 'ready' : 'empty');
    } catch (err) {
      console.warn('shared dashboard links fetch failed', err);
      setErrorMessage(userMessageForError(err, 'Could not load your shared links.'));
      setStatus('error');
    }
  }, [projectId, getHeaders]);

  /** @returns {Promise<{ ok: boolean, errorMessage?: string }>} */
  const revoke = useCallback(
    async (id) => {
      try {
        const res = await fetch(`${apiConfig.baseUrl}/api/v1/shared-dashboard-links/${encodeURIComponent(id)}`, {
          method: 'DELETE',
          headers: await getHeaders(),
        });
        const json = await res.json().catch(() => null);
        if (!res.ok || !json?.success) throw apiErrorFromResponse(res, json);
        await load();
        return { ok: true };
      } catch (err) {
        return { ok: false, errorMessage: userMessageForError(err, 'Could not revoke the link.') };
      }
    },
    [getHeaders, load],
  );

  return { status, links, errorMessage, reload: load, revoke };
}
